/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { ComponentChildren } from 'preact';
import { Text } from '../UNSAFE_Text';
import { classNames } from '../utils/UNSAFE_classNames';
import { useId } from '../hooks/UNSAFE_useId';
import { TabbableModeContext } from '../hooks/UNSAFE_useTabbableMode';
import { tableRowStyles } from './themes/TableRowStyles.css';
import { TableNoDataVariantOptions } from './themes/TableNoDataStyles.css';
import { TableNoDataRedwoodTheme } from './themes/redwood/TableNoDataTheme';
import { useComponentTheme } from '../hooks/UNSAFE_useComponentTheme';
import { BundleType } from '../resources/nls/bundle';
import { useTranslationBundle } from '../hooks/UNSAFE_useTranslationBundle';

/**
 * Props for the TableCell Component
 */
export type TableNoDataProps = {
  tableId: string;
  columnsCount: number;
  isFocusRingVisible: boolean;
  isTabbable: boolean;
  renderer?: () => ComponentChildren;
};

/**
 * The internal component used to render a single cell in a TableRow.
 */
export function TableNoData({
  tableId,
  columnsCount,
  isFocusRingVisible,
  isTabbable,
  renderer
}: TableNoDataProps) {
  const translations = useTranslationBundle<BundleType>('@oracle/oraclejet-preact');

  const rowClasses = classNames([tableRowStyles.base]);

  const { classes: themeClasses } = useComponentTheme<TableNoDataVariantOptions>(
    TableNoDataRedwoodTheme,
    {
      defaultRenderer: renderer == null ? 'isDefaultRenderer' : 'notDefaultRenderer',
      focusRingVisible: isFocusRingVisible ? 'isFocusRingVisible' : 'notFocusRingVisible'
    }
  );

  const cellClasses = classNames([themeClasses]);

  function defaultContentRenderer() {
    return (
      <Text lineClamp={1} variant="primary" size="md">
        {translations.collection_noData()}
      </Text>
    );
  }

  const contentRenderer = renderer != null ? renderer : defaultContentRenderer;

  return (
    <tr aria-rowindex={2} class={rowClasses} role={'row'}>
      <td
        aria-colindex={1}
        class={cellClasses}
        id={useId()}
        tabIndex={-1}
        role={'gridcell'}
        colSpan={columnsCount}
        data-oj-cell-type={'noData'}
        data-oj-table-focusable={tableId}
        data-oj-table-nodata-cell={tableId}>
        <TabbableModeContext.Provider value={{ isTabbable }}>
          {contentRenderer()}
        </TabbableModeContext.Provider>
      </td>
    </tr>
  );
}
